<template lang="pug">
#family-message-histroy-detail
  // title
  .d-flex.flex-row.justify-content-center.mb-1
    .page-title 送信履歴詳細
  .mb-3.separator

  // menu list
  b-card.mb-1(no-body).mb-4
    // message detail
    MessageDetailAccordion
    // unread family
    FamilyListAccordioin(v-if="_showReadUnread" :type="0")
    // read family
    FamilyListAccordioin(v-if="_showReadUnread" :type="1")
    // reply list
    MessageReplyListAccordion(v-if="_showReplyList" @onReplyMessage="onReplyMessage" @onReadSwitch="onReadSwitch")

  // buttons
  .d-flex.flex-row.justify-content-center
    b-button.btn.back-btn(@click="onBack") 戻る

  // loading modal
  Loading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore, mobileMessageReplyDetailStore } from "@/store/store";
import { MobileMessageHistoryState } from "@/store/MobileMessageHistory";
import { MobileMessageReplyDetailState } from "@/store/MobileMessageReplyDetail";
import { MobileUrl } from "@/commons/constants/MobileUrl";

import SendMessageRepliesFamiliesService, { SendMessageRepliesFamilyPatchModel } from "@/commons/services/SendMessageRepliesFamilies";
import { SendMessageReplyModel } from "@/commons/services/SendMessageReplies";

import Loading from "@/mobile/basics/Loading.vue";
import MessageDetailAccordion from "@/mobile/containers/FamilyMessageHistoryDetail/MessageDetailAccordion.vue";
import FamilyListAccordioin from "@/mobile/containers/FamilyMessageHistoryDetail/FamilyListAccordioin.vue";
import MessageReplyListAccordion from "@/mobile/containers/FamilyMessageHistoryDetail/MessageReplyListAccordion.vue";
import { MessageHistoryReplyListItem } from "@/mobile/components/lists/MessageHistoryReplyList.vue";

/**
 * ### 送信履歴詳細(家族)
 */
@Component({
  components: {
    Loading,
    MessageDetailAccordion,
    FamilyListAccordioin,
    MessageReplyListAccordion
  }
})
export default class FamilyMessageHistoryDetail extends Vue {
  /** メッセージ送信の返信サービス(家族) */
  @Inject("SendMessageRepliesFamiliesService") sendMessageRepliesFamiliesService!: SendMessageRepliesFamiliesService;

  /** ### 新規送信入力画面での入力情報 */
  @Getter("mobileMessageHistoryState") state!: MobileMessageHistoryState;

  /** 読み込み中モーダル表示可否 */
  isBusy: boolean = false;
  /** 読み込み中モーダルメッセージ */
  loadingMessage: string = "読込み中";

  get _showReadUnread(): boolean {
    return this.state.functionContents.addReadButton;
  }

  get _showReplyList(): boolean {
    return this.state.functionContents.addReplyButton;
  }

  /**
   * ### 返信の未読押下イベント
   *
   * - 返信未読を既読に更新する
   * - サイドメニューの返信未読バッジを更新する
   * - メッセージのやりとり画面へ遷移する
   */
  public async onReplyMessage(item: MessageHistoryReplyListItem): Promise<void> {
    if (item.readed == false) {
      for (const message of item.messages as Array<SendMessageReplyModel>) {
        if (message.readUserIds.indexOf(userStore.loginUser.userId) < 0 ? true : false) {
          const request: SendMessageRepliesFamilyPatchModel = {
            readUserId: userStore.loginUser.userId,
            readUserName: userStore.loginUser.name,
            read: true
          };

          await this.sendMessageRepliesFamiliesService.patch(userStore, Number(this.state.datetime), message.residentId, message.familyId, message.datetime, request);

          message.readUserIds.push(userStore.loginUser.userId);
          item.readed = true;
        }
      }
      this.$emit("refresh");
    }

    const state: MobileMessageReplyDetailState = {
      ...item,
      senderType: this.state.senderType,
      exist: true
    };
    mobileMessageReplyDetailStore.updateMobileMessageReplyDetailState({ state: state });

    const type: string = this.$route.params.type;
    this.$router.push({ name: MobileUrl.FamilyMessageReplyDetail, params: { type: type } });

    return;
  }

  /**
   * ### 既読／未読の切り替え
   */
  public async onReadSwitch(item: MessageHistoryReplyListItem): Promise<void> {
    // ローディングを表示して操作不可にしないと連打で複数かい更新が走る
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    for (const message of item.messages as Array<SendMessageReplyModel>) {
      // 変更後の値
      const afterRead: boolean = !item.readed;
      // readUserIdsに自分のユーザーIDが存在すれば現在は「既読」、存在しなければ現在は「未読」
      const currentRead: boolean = -1 < message.readUserIds.indexOf(userStore.loginUser.userId) ? true : false;
      // 変更後の値と現在の値が異なる場合のみ更新が必要
      // 例：2件返信があり、1件目は既読、2件目は未読の場合は画面上「未読」として扱っている場合、2件目だけを「既読」にしないと同じuserIdが複数登録されてしまう
      if (afterRead != currentRead) {
        const request: SendMessageRepliesFamilyPatchModel = {
          readUserId: userStore.loginUser.userId,
          readUserName: userStore.loginUser.name,
          read: afterRead
        };

        await this.sendMessageRepliesFamiliesService.patch(userStore, Number(this.state.datetime), message.residentId, message.familyId, message.datetime, request);

        if (afterRead) {
          message.readUserIds.push(userStore.loginUser.userId);
        } else {
          const index: number = message.readUserIds.indexOf(userStore.loginUser.userId);
          message.readUserIds.splice(index, 1);
        }
      }
    }
    // 最後に既読／未読を反転させる
    item.readed = !item.readed;

    this.isBusy = false;

    this.$emit("refresh");

    return;
  }

  /**
   * ### 戻るボタン押下イベント
   *
   * - 種別に応じて戻り先を切り替える
   * - 種別が意図しない値の場合はホーム画面へ遷移する
   */
  public onBack(): void {
    const type: string = this.$route.params.type;
    const page: string = this.$route.params.page;

    if (type == "1") {
      this.$router.push({ name: MobileUrl.FamilyMessageList, params: { page: page } });
    } else if (type == "2") {
      this.$router.push(MobileUrl.NewArrivalMessagesList);
    } else {
      this.$router.push(MobileUrl.Home);
    }

    return;
  }
}
</script>

<style lang="scss" scopeed>
@import "../../../styles/mobile/_colors.scss";

#family-message-histroy-detail {
  #text {
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  .preview-image,
  .preview-video {
    padding: 0.25rem;
    border: 1px solid $border-default;
    border-radius: 0.25rem;
  }
  .back-btn {
    min-width: 84px;
  }
}
</style>
